import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getTodaysBookings, updateBookingStatus } from '@/services/api';
import { Search, CheckCircle2, XCircle, LogIn, LogOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AUTO_REFRESH_INTERVAL = 30000;

const SecurityAmenities = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchBookings = useCallback(async (showToast = false) => {
    try {
      setIsRefreshing(true);
      const data = await getTodaysBookings();
      setBookings(data);
      setFilteredBookings(data);
      setLastUpdated(new Date());
      if (showToast) {
        toast.success('Bookings refreshed');
      }
    } catch (error) {
      if (showToast) {
        toast.error('Failed to load bookings');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchBookings(false);
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchBookings]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = bookings.filter(
        (b) =>
          b.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.booking_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.amenity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.user_unit?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBookings(filtered);
    } else {
      setFilteredBookings(bookings);
    }
  }, [searchTerm, bookings]);

  const handleAction = async (bookingId, status) => {
    const labels = { used: 'checked in', no_show: 'marked as no show', checked_out: 'checked out' };
    if (status === 'no_show' && !window.confirm('Mark this booking as No Show?')) return;
    try {
      await updateBookingStatus(bookingId, status);
      toast.success(`Resident ${labels[status]} successfully`);
      fetchBookings(false);
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-orange-100 text-orange-700 border-orange-200',
      used: 'bg-blue-100 text-blue-700 border-blue-200',
      checked_out: 'bg-slate-100 text-slate-700 border-slate-200',
      no_show: 'bg-red-100 text-red-700 border-red-200',
      expired: 'bg-slate-100 text-slate-500 border-slate-200'
    };
    return colors[status] || colors.confirmed;
  };

  const getStatusLabel = (status) => {
    const labels = { confirmed: 'Pending Check-In', used: 'Checked In', checked_out: 'Checked Out', no_show: 'No Show', expired: 'Expired' };
    return labels[status] || status;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="security-amenities">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Amenity Check-In</h1>
          <p className="text-sm md:text-base text-slate-600">Check in and check out residents for amenity bookings</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchBookings(true)}
            disabled={isRefreshing}
            data-testid="refresh-bookings-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search by name, unit, amenity, or booking code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
              data-testid="amenity-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bookings List */}
      <div className="grid gap-4">
        {filteredBookings.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No bookings found</p>
            </CardContent>
          </Card>
        ) : (
          filteredBookings.map((booking) => (
            <Card key={booking.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="amenity-booking-card">
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <h3 className="text-lg md:text-xl font-semibold text-slate-900">{booking.user_name}</h3>
                      <Badge className={getStatusColor(booking.status)}>
                        {getStatusLabel(booking.status)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 text-sm mb-3">
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm">Unit</p>
                        <p className="font-medium text-slate-900">{booking.user_unit || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm">Amenity</p>
                        <p className="font-medium text-slate-900">{booking.amenity_name}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm">Date & Time</p>
                        <p className="font-medium text-slate-900">{new Date(booking.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} | {booking.time_slot}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm">Booking Code</p>
                        <p className="font-bold text-secondary font-mono text-base">{booking.booking_code}</p>
                      </div>
                    </div>
                    {booking.checked_in_at && (
                      <p className="text-xs text-slate-500">Checked in at {new Date(booking.checked_in_at).toLocaleTimeString()} by {booking.checked_in_by}</p>
                    )}
                    {booking.checked_out_at && (
                      <p className="text-xs text-slate-500">Checked out at {new Date(booking.checked_out_at).toLocaleTimeString()} by {booking.checked_out_by}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {booking.status === 'confirmed' && booking.payment_status === 'paid' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleAction(booking.id, 'used')}
                          data-testid="checkin-amenity-btn"
                        >
                          <LogIn className="h-4 w-4 mr-1" />
                          Check In
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleAction(booking.id, 'no_show')}
                          data-testid="noshow-amenity-btn"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          No Show
                        </Button>
                      </>
                    )}
                    {booking.status === 'confirmed' && booking.payment_status !== 'paid' && booking.payment_status !== 'no_bill' && (
                      <Badge className="bg-red-100 text-red-700 border-red-200 py-1.5 px-3" data-testid="payment-pending-badge">
                        Payment Pending
                      </Badge>
                    )}
                    {booking.status === 'confirmed' && booking.payment_status === 'no_bill' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleAction(booking.id, 'used')}
                          data-testid="checkin-amenity-btn"
                        >
                          <LogIn className="h-4 w-4 mr-1" />
                          Check In
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleAction(booking.id, 'no_show')}
                          data-testid="noshow-amenity-btn"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          No Show
                        </Button>
                      </>
                    )}
                    {booking.status === 'used' && (
                      <Button
                        size="sm"
                        className="bg-slate-600 hover:bg-slate-700 text-white"
                        onClick={() => handleAction(booking.id, 'checked_out')}
                        data-testid="checkout-amenity-btn"
                      >
                        <LogOut className="h-4 w-4 mr-1" />
                        Check Out
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SecurityAmenities;
